import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { Box, Button, Chip, Fade, IconButton, Tooltip } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import ArticleIcon from '@mui/icons-material/Article'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import useMediaQuery from '@mui/material/useMediaQuery'

import useCompareMode from '../CompareViewPanel/useCompareMode'
import { useZustand } from '../../hooks/useZustand'
import { UserContext } from '../../UserContext'
import { useFilterStore } from '../../stores/useFilterStore'
import { useBootstrapStore } from '../../stores/useBootstrapStore'
import { usePanelStore } from '../../stores/usePanelStore'
import { useAdditionalContexts } from '../../hooks/useAdditionalContexts'
import { useGraphStore } from '../../stores/useGraphStore'
import { isInIframe } from '../LeftMenuMigration/legacyBridge'

import MergedFilterPanel from '../MergedFilterPanel'
import StatementEditor from '../StatementEditor'
import CompareViewPanel from '../CompareViewPanel'
import AdaptiveButton from '../AdaptiveButton'

import SelectedFilters from '../MergedFilterPanel/SelectedFilters'

const updateStatementsWithTopicsSelector = state =>
    state.updateStatementsWithTopics

const updateStatementsViewTypeSelector = state => state.updateStatementsViewType

const StatementFilterPanel = ({
    isAppBarOpen,
    isStatementEditorOpen,
    setIsStatementEditorOpen,
}) => {
    // Compact statement-control layout for narrow viewports OR when embedded
    // in an iframe — embeds get the mobile-style "Context" pill + chevron
    // regardless of the host window's width.
    const isNarrow = useMediaQuery('(max-width:830px)')
    const [inIframe] = useState(() => isInIframe())
    const isMobile = isNarrow || inIframe
    const isMobileMoreOpen = usePanelStore(
        state => state.isMobileFilterOptionsOpen
    )
    const toggleMobileMore = usePanelStore(
        state => state.toggleMobileFilterOptions
    )
    const setMobileMoreOpen = usePanelStore(
        state => state.setMobileFilterOptionsOpen
    )
    const [selectedComparisonView, setSelectedComparisonView] = useState(null)
    const addContextParams = useAdditionalContexts()

    const { flags = {}, flagsLoading = false } = React.useContext(UserContext)

    const { getCompareChipLabel, isCompareMode } = useCompareMode()

    const currentContext = useZustand(state => state.currentContext)

    const loadedContextList = useZustand(state => state.loadedContextList)

    const allStatements = useZustand(state => state.statements)
    const currentStatements = allStatements[currentContext] || []

    const [modularityExecuted, setModularityExecuted] = useState(false)

    const updateStatementsWithTopics = useZustand(
        updateStatementsWithTopicsSelector
    )
    const synthesisType = useBootstrapStore(state => state.synthesisType)
    const isSynthesisMode = synthesisType != null
    const viewType = useBootstrapStore(state => state.viewType)
    const isSearchMode = viewType === 'search'

    // Compare/synthesis/search span multiple contexts; flatten the buckets
    // since `currentContext` is null and would otherwise yield an empty list.
    const finalStatements =
        isCompareMode || isSynthesisMode || isSearchMode
            ? Object.values(allStatements).flat()
            : currentStatements

    const isThereStatements = finalStatements.length > 0

    const topicsLoaded = useZustand(state => state.loadedTopics)
    const loadedStatements = useZustand(state => state.loadedStatements)

    const updateStatementsViewType = useZustand(
        updateStatementsViewTypeSelector
    )

    const [compareDialogOpen, setCompareDialogOpen] = useState(false)

    // Set by the empty-graph "Add statements" link so the editor opens even
    // with zero statements (its normal gate requires `isThereStatements`),
    // giving the user a place to add the first statement by hand.
    const [forceShowEmptyEditor, setForceShowEmptyEditor] = useState(false)

    useEffect(() => {
        if (!isMobile && isMobileMoreOpen) {
            setMobileMoreOpen(false)
        }
    }, [isMobile])

    const addStatementRequested = useZustand(
        state => state.addStatementRequested
    )

    // Empty-graph "Add statements" intent: open the panel even with zero
    // statements. StatementEditor reveals the form and clears the request.
    useEffect(() => {
        if (!addStatementRequested) return
        setIsStatementEditorOpen(true)
        setForceShowEmptyEditor(true)
    }, [addStatementRequested, setIsStatementEditorOpen])

    // Drop the forced-empty override once the panel is closed so reopening
    // an empty graph's editor goes back to its normal (hidden) behavior.
    useEffect(() => {
        if (!isStatementEditorOpen && forceShowEmptyEditor) {
            setForceShowEmptyEditor(false)
        }
    }, [isStatementEditorOpen, forceShowEmptyEditor])

    useEffect(() => {
        if (window.myEventEmitter) {
            const handleCompareContexts = () => {
                setCompareDialogOpen(true)
            }

            myEventEmitter.on('compareContexts', handleCompareContexts)

            return () => {
                if (window.myEventEmitter) {
                    myEventEmitter.removeListener(
                        'compareContexts',
                        handleCompareContexts
                    )
                }
            }
        }
    }, [])

    // Trigger side effects when statements are loaded (by bootstrap)
    // Note: Actual data fetching is now handled by bootstrap store in App.jsx
    useEffect(() => {
        if (!loadedStatements || !loadedContextList) return
        if (flagsLoading) return

        console.log('[InfraNodus] Statements loaded, triggering side effects')

        // Skip old sigma-based operations when using new 3D graph
        if (!flags.new_3d_graph && typeof analyticsPanel !== 'undefined') {
            analyticsPanel.updateStatementsGraphStats()
            if (typeof networkStructurePanel !== 'undefined') {
                networkStructurePanel.updateAllCharts()
            }
            if (
                typeof filterPanel !== 'undefined' &&
                typeof sigma !== 'undefined'
            ) {
                filterPanel.updatePanel(
                    sigma.instances(0).graph,
                    filterPanel.getFilter()
                )
            }
            if (typeof graphFactory !== 'undefined') {
                graphFactory.sendPinnedNodesSelections()
            }
        }

        // Always generate AI advice
        if (typeof analyticsPanel !== 'undefined') {
            analyticsPanel.generateAiAdvice()
        }

        if (window.myEventEmitter) {
            if (myEventEmitter._events['statementsLoaded']) {
                console.log('emit update statements')
                window.myEventEmitter.emit('statementsLoaded')
            }
        }
    }, [loadedStatements, loadedContextList, flags.new_3d_graph, flagsLoading])

    useEffect(() => {
        if (!topicsLoaded || !loadedStatements) return

        let statementsToSet
        if (isCompareMode) {
            // In compare mode the backend already returned the merged/diff/intersection graph.
            // Collect statements from all loaded contexts so all are visible.
            statementsToSet = Object.values(allStatements).flat()
        } else {
            statementsToSet = currentStatements
        }

        if (!statementsToSet || statementsToSet.length === 0) {
            return
        }
        useFilterStore.getState().setFilteredStatements(statementsToSet)
    }, [topicsLoaded, loadedStatements, allStatements, isCompareMode])

    // this is the function we ensure that the statements are updated after graph modularity is updated

    useEffect(() => {
        // Set up a listener to update statements with new topic IDs on reload
        // TODO: why this is being called when new statement is added?
        const updateStatementsGraphData = async () => {
            await updateStatementsWithTopics()
            // TODO: remove this and related functions when new graph is ready
            if (!flags.new_3d_graph) {
                await analyticsPanel.updateStatementsGraphStats()
                networkStructurePanel.updateAllCharts()

                filterPanel.updatePanel(
                    sigma.instances(0).graph,
                    filterPanel.getFilter()
                )
            }
            setModularityExecuted(true)
        }

        if (window.myEventEmitter) {
            myEventEmitter.on('Modularity', updateStatementsGraphData)
        }
        return () => {
            if (window.myEventEmitter) {
                myEventEmitter.removeListener(
                    'Modularity',
                    updateStatementsGraphData
                )
            }
        }
    }, [])

    useEffect(() => {
        const runAfterModularity = async () => {
            if (modularityExecuted) {
                await analyticsPanel.updateStatementsGraphStats()
                analyticsPanel.generateAiAdvice()
                setModularityExecuted(false)
            }
        }
        runAfterModularity()
    }, [modularityExecuted, finalStatements])

    // Apply after UI refactoring
    // let filterButtonLeftPadding = isAppBarOpen ? '330px' : '0'
    // Desktop sits flush-left at 8px to match GraphToolsBar; mobile keeps
    // the existing 50px container origin (the Show Content pill then uses
    // marginLeft: -46px to extend further left to align with PanelToggles).
    const closedLeft = isMobile ? '50px' : '8px'
    let filterButtonLeftPadding = isAppBarOpen ? '340px' : closedLeft
    const isEditorVisible =
        isStatementEditorOpen && (isThereStatements || forceShowEmptyEditor)
    if (isEditorVisible) filterButtonLeftPadding = closedLeft

    const setIsOpen = useMemo(() => {
        return () => {
            setIsStatementEditorOpen(!isStatementEditorOpen)
            if (!isStatementEditorOpen) {
                $('#statementmenu').addClass('statementEditorOpen')
            } else {
                $('#statementmenu').removeClass('statementEditorOpen')
            }
        }
    }, [isStatementEditorOpen])
    if (!loadedStatements) return null

    return (
        <Box id="categorySwitch">
            <Box
                sx={{
                    position: 'fixed',
                    left: filterButtonLeftPadding,
                    // backgroundColor: 'rgba(255, 255, 255, 0.5)',
                    top: '48px',
                    // width: '100%',
                    gap: 1,
                    paddingTop: '10px',
                    // APply after UI refactoring
                    // paddingLeft: 1,
                    // paddingBottom: 1,
                    display: 'flex',
                    maxWidth: isMobile ? '300px' : '700px',
                    flexWrap: 'wrap',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    transition: theme =>
                        theme.transitions.create(['right', 'left'], {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.leavingScreen,
                        }),
                    zIndex: theme => theme.zIndex.drawer - 1,
                }}
            >
                {isMobile ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0,
                            // Extend the whole pill+chevron group leftward to
                            // align with the PanelToggles buttons at the
                            // bottom-left (their outer left ≈ 4px from
                            // screen, this row's container starts at 50px).
                            marginLeft: '-46px',
                        }}
                    >
                        <Tooltip
                            title={
                                isStatementEditorOpen
                                    ? 'Hide Content'
                                    : 'Show Content'
                            }
                        >
                            <Button
                                onClick={setIsOpen}
                                color="primary"
                                size="small"
                                variant={
                                    isStatementEditorOpen
                                        ? 'outlined'
                                        : 'contained'
                                }
                                endIcon={<ArticleIcon />}
                                aria-label={
                                    isStatementEditorOpen
                                        ? 'Hide Content'
                                        : 'Show Content'
                                }
                                sx={{
                                    my: 0,
                                    borderRadius: '10px',
                                    textTransform: 'none',
                                    whiteSpace: 'nowrap',
                                    paddingLeft: '14px',
                                    paddingRight: '14px',
                                }}
                            >
                                Content
                            </Button>
                        </Tooltip>
                        <Tooltip
                            title={
                                isMobileMoreOpen
                                    ? 'Hide options'
                                    : 'More options'
                            }
                        >
                            <IconButton
                                color="primary"
                                onClick={toggleMobileMore}
                                aria-label={
                                    isMobileMoreOpen
                                        ? 'Hide options'
                                        : 'More options'
                                }
                                aria-expanded={isMobileMoreOpen}
                                sx={{
                                    my: 0,
                                    padding: '2px',
                                    transition: 'transform 0.2s ease-in-out',
                                    transform: isMobileMoreOpen
                                        ? 'rotate(180deg)'
                                        : 'rotate(0deg)',
                                }}
                            >
                                <KeyboardArrowDownIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                ) : (
                    <AdaptiveButton
                        onClick={setIsOpen}
                        buttonLabel={
                            isStatementEditorOpen
                                ? 'Hide Content'
                                : 'Show Content'
                        }
                        tooltipTitle={
                            isStatementEditorOpen
                                ? 'Hide Content'
                                : 'Show Content'
                        }
                        iconName="ArticleIcon"
                        variant="outlined"
                        isActive={!isStatementEditorOpen}
                    />
                )}

                {isMobile ? (
                    isMobileMoreOpen && (
                        <Box
                            sx={{
                                // Force the stack onto its own line below the
                                // Show Content pill + chevron, and align it
                                // with the pill's left edge (which uses
                                // marginLeft: -46px to reach screen ~4px).
                                width: '100%',
                                marginLeft: '-46px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: 1,
                                mt: 0.5,
                                // MergedFilterPanel and CompareViewPanel wrap
                                // their AdaptiveButton in a Box with
                                // justifyContent: center + maxWidth, which
                                // would offset the buttons differently from
                                // each other and from the pill above. Force
                                // every direct child to start at the column's
                                // left edge with no width cap so all four
                                // buttons share the same x position.
                                '& > *': {
                                    maxWidth: 'none !important',
                                    width: 'auto',
                                    justifyContent: 'flex-start !important',
                                },
                            }}
                        >
                            <MergedFilterPanel forceFullButton />
                            <CompareViewPanel
                                selectedComparisonView={selectedComparisonView}
                                onComparisonViewChange={
                                    setSelectedComparisonView
                                }
                                addContextParams={addContextParams}
                                forceFullButton
                            />
                        </Box>
                    )
                ) : (
                    <>
                        <MergedFilterPanel />
                        <CompareViewPanel
                            selectedComparisonView={selectedComparisonView}
                            onComparisonViewChange={setSelectedComparisonView}
                            addContextParams={addContextParams}
                        />
                        {/* On desktop, AI: Summarize lives in the statement
                            panel's footer (StatementEditor/Footer) */}
                    </>
                )}
                {isCompareMode && (
                    <Tooltip
                        title={getCompareChipLabel(selectedComparisonView)}
                    >
                        <Chip
                            deleteIcon={<DeleteIcon />}
                            size="small"
                            label={getCompareChipLabel(selectedComparisonView)}
                            onDelete={() => {
                                console.log('Returning to the original graph')

                                // without query params
                                const newLocation = location.pathname
                                window.location = newLocation
                                // TODO: use navigate to keep the history
                                // navigate({
                                //     to: newLocation,
                                //     options: { replace: true },
                                // })
                            }}
                            color="primary"
                            sx={{
                                minWidth: '200px',
                            }}
                        />
                    </Tooltip>
                )}
                <Box
                    sx={{
                        // Full width so the chips always break onto their own
                        // line below the buttons (the toolbar is a wrapping
                        // flex row — without this a single chip fits inline
                        // to the right of the buttons).
                        width: '100%',
                        // On mobile the toolbar container starts at left:50px
                        // while the Context pill shifts back with
                        // marginLeft:-46px to reach the screen's left edge.
                        // Pull the selected topic chips back too, but keep a
                        // small left gap (like the menu below) and nudge them
                        // down so they line up with the controls on the right.
                        ...(isMobile && {
                            marginLeft: '-38px',
                            mt: 1,
                        }),
                    }}
                >
                    <SelectedFilters />
                </Box>
                <Fade in={isEditorVisible} timeout={300}>
                    <Box
                        sx={{
                            position: 'fixed',
                            // Apply after UI refactoring
                            // left: '0px',
                            // top: '177px',
                            left: isMobile ? '70px' : '50px',
                            top: '174px',
                            maxHeight: '74dvh',
                            width: isMobile ? '300px' : '328px',
                            backgroundColor: '#fff',
                            borderRadius: '14px',
                            zIndex: theme => theme.zIndex.drawer,
                            transition: 'transform 0.3s ease-in-out',
                            boxShadow: '0 5px 40px rgba(0, 0, 0, .16)',
                        }}
                    >
                        <StatementEditor
                            isCompareMode={isCompareMode}
                            isSynthesisMode={isSynthesisMode}
                            isSearchMode={isSearchMode}
                            viewMode={selectedComparisonView}
                            isOpen={isStatementEditorOpen}
                            setIsOpen={setIsOpen}
                        />
                    </Box>
                </Fade>
            </Box>
        </Box>
    )
}

export default StatementFilterPanel
